import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Supplier } from "../types/api";
import { FC } from "react";
import { Box, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { dataTables } from "src/styles";
import ButtonWithConfirm from "./modal.confirm-dialog.component";

interface Props {
  data: Supplier[];
  pageSize: number;
  totalRows: number;
  currentPage: number;
  onPageChange: Function;
  onPageSizeChange: Function;
  onRowClick?: (supplier: Supplier) => void;
  onDelete?: (supplier: Supplier) => void;
  onArchive?: (supplier: Supplier) => void;
  onUnarchive?: (supplier: Supplier) => void;
  loading?: boolean;
}

const plural = (n: number, noun: string) => `${n} ${noun}${n !== 1 ? "s" : ""}`;

// The rules: products block everything (move them first); purchase orders
// block delete but not archive; nothing referencing it allows either.
const deleteBlockedReason = (s: Supplier): string | null => {
  const products = s.productCount || 0;
  const pos = s.purchaseOrderCount || 0;
  const reasons: string[] = [];
  if (products > 0) reasons.push(plural(products, "product"));
  if (pos > 0) reasons.push(plural(pos, "purchase order"));
  return reasons.length > 0 ? `Cannot delete: still has ${reasons.join(" and ")}` : null;
};

const archiveBlockedReason = (s: Supplier): string | null => {
  const products = s.productCount || 0;
  return products > 0
    ? `Cannot archive: still has ${plural(products, "product")}. Move them to another supplier first.`
    : null;
};

const SupplierTable: FC<Props> = (props: Props) => {
  const columns: GridColDef[] = [
    { field: "id" },
    { field: "guid" },
    {
      field: "name",
      headerName: "Name",
      minWidth: 100,
      flex: 0.25,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            {params.value}
          </Typography>
          {params.row.archivedAt && (
            <Chip
              label="archived"
              size="small"
              sx={{ flexShrink: 0 }}
              title={`Archived ${new Date(params.row.archivedAt).toLocaleDateString()}. Hidden from supplier pickers; kept for order history.`}
              data-testid="archived-supplier-pill"
            />
          )}
        </Box>
      ),
    },
    { field: "salesRep", headerName: "Sales Rep", flex: 0.25 },
    { field: "salesRepEmail", headerName: "Sales Rep Email", flex: 0.25 },
    { field: "salesRepPhone", headerName: "Sales Rep Phone", flex: 0.25 },
    {
      field: "productCount",
      headerName: "Products",
      type: "number",
      minWidth: 100,
      flex: 0.12,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "purchaseOrderCount",
      headerName: "POs",
      type: "number",
      minWidth: 80,
      flex: 0.1,
      align: "right",
      headerAlign: "right",
    },
  ];

  if (props.onDelete || props.onArchive) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      minWidth: 130,
      flex: 0.15,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const supplier: Supplier = params.value;
        const deleteBlocked = deleteBlockedReason(supplier);
        const archiveBlocked = archiveBlockedReason(supplier);
        return (
          <div onClick={(event) => event.stopPropagation()}>
            {props.onArchive && !supplier.archivedAt && (
              <Tooltip title={archiveBlocked || "Archive supplier"}>
                <span>
                  <ButtonWithConfirm
                    renderAsIcon
                    color="primary"
                    disabled={!!archiveBlocked}
                    title="Archive supplier?"
                    message={`"${supplier.name}" will no longer be offered when raising purchase orders or editing products. Its ${plural(
                      supplier.purchaseOrderCount || 0,
                      "existing purchase order"
                    )} keep it, and it can be unarchived later.`}
                    okButtonLabel="Archive"
                    onOk={() => props.onArchive && props.onArchive(supplier)}
                  >
                    <ArchiveIcon />
                  </ButtonWithConfirm>
                </span>
              </Tooltip>
            )}
            {props.onUnarchive && supplier.archivedAt && (
              <Tooltip title="Unarchive supplier">
                <IconButton
                  color="primary"
                  aria-label="Unarchive supplier"
                  onClick={() => props.onUnarchive && props.onUnarchive(supplier)}
                >
                  <UnarchiveIcon />
                </IconButton>
              </Tooltip>
            )}
            {props.onDelete && (
              <Tooltip title={deleteBlocked || "Delete supplier"}>
                <span>
                  <ButtonWithConfirm
                    renderAsIcon
                    color="primary"
                    disabled={!!deleteBlocked}
                    message={`This will permanently delete the supplier "${supplier.name}" along with its contacts, address and discount rules. This cannot be undone!`}
                    okButtonLabel="Delete"
                    onOk={() => props.onDelete && props.onDelete(supplier)}
                  >
                    <DeleteIcon />
                  </ButtonWithConfirm>
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    });
  }

  const rows =
    props.data && props.data.length > 0
      ? props.data.map((s: Supplier, index: number) => {
          return {
            id: s.id || index, // Fallback to index if id is undefined
            guid: s.guid,
            name: s.name,
            salesRep: s.salesRep || "",
            salesRepEmail: s.salesRepEmail || "",
            salesRepPhone: s.salesRepPhone || "",
            productCount: s.productCount ?? 0,
            purchaseOrderCount: s.purchaseOrderCount ?? 0,
            archivedAt: s.archivedAt || null,
            actions: s,
          };
        })
      : [];

  return (
    <>
      <Paper
        component="form"
        sx={{ p: "2px 4px", display: "flex", alignItems: "center" }}
      >
        <DataGrid
          loading={props.loading}
          slots={{ loadingOverlay: () => <CircularProgress /> }}
          rows={rows}
          columns={columns}
          paginationModel={{ page: props.currentPage, pageSize: props.pageSize }}
          rowCount={props.totalRows}
          paginationMode="server"
          density="compact"
          onPaginationModelChange={(model) => {
            props.onPageChange(model.page);
            props.onPageSizeChange(model.pageSize);
          }}
          onRowClick={(params) => {
            if (props.onRowClick) {
              const supplier = props.data.find((s) => s.guid === params.row.guid);
              if (supplier) {
                props.onRowClick(supplier);
              }
            }
          }}
          columnVisibilityModel={{
            id: false,
            guid: false,
          }}
          sx={{ ...dataTables, "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Paper>
    </>
  );
};

export default SupplierTable;
